import React, { useCallback, useState } from 'react';
import T from 'i18n';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Menu from '@mui/material/Menu';
import Tooltip from '@mui/material/Tooltip';
import type { RealTimeTheme } from '../theme';

type ExportMenuProps = {
  disabled?: boolean;
  onExportPdf: () => void;
  onExportTxt: () => void;
  theme?: RealTimeTheme;
};

const ExportMenu: React.FC<ExportMenuProps> = ({ disabled = false, onExportPdf, onExportTxt, theme }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const menuId = 'real-time-export-menu';

  const handleOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handlePdf = useCallback(() => {
    onExportPdf();
    setAnchorEl(null);
  }, [onExportPdf]);

  const handleTxt = useCallback(() => {
    onExportTxt();
    setAnchorEl(null);
  }, [onExportTxt]);

  return (
    <div className="real-time__export">
      <Tooltip
        title="Export transcripts"
        componentsProps={{
          tooltip: { className: theme === 'dark' ? 'real-time-tooltip-dark' : '' },
          arrow: { className: theme === 'dark' ? 'real-time-tooltip-dark-arrow' : '' },
        }}
      >
        <button
          type="button"
          className="real-time__export-btn"
          onClick={handleOpen}
          disabled={disabled}
          aria-label={T.translate('case.realTimeExport', { defaultValue: 'Export transcript' }) as string}
          aria-controls={open ? menuId : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
        >
          <FileDownloadOutlinedIcon fontSize="small" />
        </button>
      </Tooltip>
      <Menu
        id={menuId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disablePortal
        transitionDuration={0}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{
          'aria-label': T.translate('case.realTimeExport', {
            defaultValue: 'Export transcript',
          }) as string,
        }}
        PaperProps={{ className: 'real-time__export-menu' }}
      >
        <div className="real-time__export-menu-section">
          <button type="button" className="real-time__export-menu-item" onClick={handlePdf}>
            PDF
          </button>
          <button type="button" className="real-time__export-menu-item" onClick={handleTxt}>
            TXT
          </button>
        </div>
      </Menu>
    </div>
  );
};

export default ExportMenu;
